import { useMemo } from "react";
import { useQueryClient } from "react-query";
import { CircularProgress } from "@mui/material";
import { useIntercom } from "react-use-intercom";

import UnclaimedBackground from "./unclaimed-background";
import AmountOdometer from "../../Components/Others/amount-odometer";
import PrimaryButton from "../../Components/Buttons/primary-button";

import renderStatusModal from "../../Organisms/Modals/status-modal";

import { useUnclaimedTransactions, useUserAccount } from "../../CustomHooks/Queries";

import { useClaimUnclaimedTransactions } from "../../CustomHooks/Mutations";

import { useModalContext } from "../../Contexts/ModalContext";
import { useViewport } from "../../Contexts/ViewportContext";

import { dispatchAPIErrorToast, getNumberFromCurrentAmount } from "../../Utils/helpers";
import { APIQueryKeys, mediumScreenStartFrom } from "../../Utils/constants";

import { CircleExclamation } from "../../Assets/Icons";

import "./unclaimed.scss";
import "./unclaimed-responsive.scss";

export default function Unclaimed() {
  const { width } = useViewport();

  const queryClient = useQueryClient();
  const { show: showIntercom } = useIntercom();

  const modalContext = useModalContext();
  const { onModalClose } = modalContext;

  const { data: unclaimedTransactionsData } = useUnclaimedTransactions();
  const { data: userAccount } = useUserAccount();

  const { isLoading: isClaimingCashback, mutate: claimCashback } = useClaimUnclaimedTransactions({
    onSuccess: () => queryClient.resetQueries(APIQueryKeys.unclaimedTransactions),
    onError: (err) => {
      if (err?.response?.status === 422) {
        queryClient.resetQueries(APIQueryKeys.unclaimedTransactions);
        renderStatusModal(modalContext, {
          StatusIcon: CircleExclamation,
          statusClassName: "alert-error",
          title: err?.response?.data?.data?.title,
          description: err?.response?.data?.data?.description,
          CTAButtonText: "Contact Support",
          secondaryCTAButtonText: "Close",
          onCTAButtonClick: () => {
            showIntercom();
            onModalClose();
          },
          onSecondaryCTAButtonClick: () => onModalClose(),
        });
      } else {
        dispatchAPIErrorToast({ key: "claimCashback" });
      }
    },
  });

  const formattedCashBackAmount = unclaimedTransactionsData?.data?.data?.total_cash_back_unlocked ?? "$0";

  const totalCashBack = useMemo(() => getNumberFromCurrentAmount(formattedCashBackAmount), [formattedCashBackAmount]);

  return (
    <div className="unclaimed">
      <UnclaimedBackground />
      <div className="page-content">
        <div className="unclaimed-content">
          <h3 className="text-3xl-medium color--mist text-center mb-4">
            Welcome, {userAccount?.data?.data?.user?.firstName}!
          </h3>
          <div className="unclaimed-total-card mb-4">
            <p className="text-lg-regular color--mist m-0">You have</p>
            <AmountOdometer
              className="mt-1"
              value={totalCashBack}
              size={width > mediumScreenStartFrom ? "48px" : "40px"}
              fontWeight={600}
              color="var(--primary-400)"
            />
          </div>
        </div>
        <PrimaryButton className="claim-cta-button" onClick={claimCashback} disabled={isClaimingCashback}>
          Claim your {formattedCashBackAmount} {isClaimingCashback && <CircularProgress />}
        </PrimaryButton>
      </div>
    </div>
  );
}
